import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { ADSENSE, adsenseLiberado } from '../../config/adsense';
import { PLACEMENT_GRADE, campanhaDoPlacement } from '../../config/publicidade';
import { conteudoDoPlacement, useEstadoAdsense } from './estadoAdsense';
import StoreAdCard from './StoreAdCard';

/**
 * Placement de publicidade no meio da grade de ofertas (um a cada ADSENSE.intervaloGrade).
 *
 * Prioridade, nesta ordem, e nunca dois ao mesmo tempo:
 *   1. Google AdSense real (`loading`, `filled`). Enquanto o Google não responde, o espaço
 *      fica reservado com o rótulo "Anúncio". `filled`: só o anúncio do Google.
 *   2. Bloco comprovadamente vazio (`unfilled`, ou `script_timeout`: o adsbygoogle.js não
 *      processou o bloco em 5 s — ver estadoAdsense.js): o <ins> é escondido (exemplo 1
 *      da ajuda do AdSense) e o mesmo espaço recebe `house_ad` (StoreAdCard, rótulo
 *      "Publicidade") ou, a cada 3 placements, `busca` (atalho do site).
 *      Se o Google preencher depois, o anúncio dele volta a ser o único.
 *
 * - O script adsbygoogle.js é carregado UMA vez, no <head> (public/index.html). Este
 *   componente nunca injeta script: só registra o próprio <ins> na fila, uma vez.
 * - Tamanho fixo por CSS (largura da célula, altura por breakpoint): o espaço existe antes
 *   de qualquer resposta, e trocar Google por anúncio interno não move a grade.
 * - Não mexe no iframe nem no criativo, não recarrega anúncio e não clica em nada.
 * - Fora do domínio de produção (localhost, preview, testes) nenhum <ins> existe e nada vai
 *   ao Google: o espaço mostra direto o conteúdo do Super Vitrine.
 */
export default function AnuncioGrade({ posicao }) {
    const liberado = adsenseLiberado();
    const insRef = useRef(null);
    const estado = useEstadoAdsense(insRef, liberado);
    const campanhaDaPosicao = campanhaDoPlacement(posicao);
    const conteudo = conteudoDoPlacement({ liberado, estado, temCampanha: Boolean(campanhaDaPosicao) });
    const semGoogle = conteudo !== 'google';
    const campanha = conteudo === 'house_ad' ? campanhaDaPosicao : null;

    useEffect(() => {
        if (!liberado) return;
        const ins = insRef.current;
        if (!ins) return;
        // O próprio adsbygoogle marca o <ins> ao processá-lo. Sem essa marca, é a
        // primeira vez: registra. Com ela (StrictMode, re-render), não repete.
        if (!ins.getAttribute('data-adsbygoogle-status')) {
            try {
                (window.adsbygoogle = window.adsbygoogle || []).push({});
            } catch (e) {
                // Falha do Google (bloqueador, rede) não pode derrubar a grade.
            }
        }
    }, [liberado]);

    const rotuloAria = { google: 'Anúncio', house_ad: 'Publicidade', busca: 'Buscar ofertas' }[conteudo];

    return (
        <aside
            className={`sv-anuncio ${semGoogle ? 'is-sem-anuncio' : ''}`}
            aria-label={rotuloAria}
            data-testid={`anuncio-grade-${posicao}`}
            data-conteudo={conteudo}
            data-adsense-estado={liberado ? estado : 'fora_de_producao'}
        >
            {!semGoogle && <span className="sv-anuncio-rotulo">Anúncio</span>}
            {liberado && (
                <ins
                    ref={insRef}
                    className="adsbygoogle sv-anuncio-ins"
                    style={{ display: 'block' }}
                    data-ad-client={ADSENSE.client}
                    data-ad-slot={ADSENSE.slotGrade}
                />
            )}
            {campanha && <StoreAdCard campanha={campanha} posicao={posicao} placement={PLACEMENT_GRADE} />}
            {conteudo === 'busca' && (
                <div className="sv-anuncio-casa" data-testid="anuncio-sem-preenchimento">
                    <Search className="w-6 h-6" aria-hidden="true" />
                    <p>Procurando algo específico?</p>
                    <Link to="/busca" className="sv-btn-ghost">Buscar ofertas</Link>
                </div>
            )}
        </aside>
    );
}
